import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  ApiAssetDealingFrequency,
  ApiAssetDefaultConfiguration,
  ApiAssetDetails,
  ApiAssetDetailsApiPaginationResult,
  ApiAssetExDates,
  ApiAssetPriceWithHistory,
  ApiAssetProtectionClassification,
  ApiAssetType,
  ApiFee,
  ApiMultiplierType,
  ApiMutualFundAsset,
  ApiSegregatedPortfolioAsset,
  ApiStrategyTemplate,
  ApiTaxClassification,
  ApiVariableMultiplier
} from 'api/rtk-api'
import { TablePaginationParams } from 'components/core-v2/molecules/table-pagination'
import { set } from 'lodash'
import { all, call, put, PutEffect, select, takeLatest } from 'redux-saga/effects'

import api from '@/api/_legacy'
import { defaultQueryParams, QueryParams } from '@/api/_legacy/types'
import { TODAY } from '@/const'
import { createLogoutExtraReducers } from '@/utilities/redux'

import {
  Errors,
  extractErrorMessagesFromServerResponse,
  extractErrorMessagesFromServerResponseToArray
} from '../../tradebuilder-v2/util'
import { mapAssetToMutualFund, mapAssetToSegPortfolio } from './mappers'
import { validate } from './validation-rules'

const TENANT_ID = 'absa'

export type EditAssetsState = {
  assetPriceHistory?: ApiAssetPriceWithHistory
  assets: ApiAssetDetails[]
  availableAssetClasses: string[]
  availableCptyIds: string[]
  availableCurrencies: string[]
  availableStrategies: ApiStrategyTemplate[]
  canProceed: boolean
  currentPageNumber: number
  errors: Errors
  exDateNotification: string
  isLoading: boolean
  isNew: boolean
  numTotalAssets: number
  query: QueryParams
  saveSuccess: boolean
  selectedAsset?: ApiAssetDetails
  serverErrors?: string[]
}

const initialState: EditAssetsState = {
  canProceed: false,
  saveSuccess: false,
  numTotalAssets: 0,
  currentPageNumber: 1,
  assets: [],
  availableStrategies: [],
  availableAssetClasses: [],
  availableCurrencies: [],
  availableCptyIds: [],
  exDateNotification: undefined,
  isNew: false,
  errors: {},
  query: { ...defaultQueryParams, PageSize: 20 },
  isLoading: false
}

function getTodayAsString() {
  return `${TODAY.getFullYear()}-${TODAY.getMonth() + 1}-${TODAY.getDate()}`
}

export const editAssetsSlice = createSlice({
  name: 'editAssets',
  initialState,
  reducers: {
    init: state => {
      state.isLoading = true
    },
    saveError: (state, action: PayloadAction<any>) => {
      state.serverErrors = extractErrorMessagesFromServerResponseToArray(action.payload)
    },
    fetchAssetDetailSuccess: (state, action: PayloadAction<ApiAssetDetailsApiPaginationResult>) => {
      state.numTotalAssets = action.payload.totalDocuments
      state.assets = action.payload.data
      state.isLoading = false
    },
    fetchAssetPriceHistorySuccess: (state, action: PayloadAction<ApiAssetPriceWithHistory>) => {
      state.assetPriceHistory = action.payload
      state.isLoading = false
    },
    fetchStrategiesSuccess: (state, action: PayloadAction<ApiStrategyTemplate[]>) => {
      state.availableStrategies = action.payload
      state.isLoading = false
    },
    fetchCptyIdsSuccess: (state, action: PayloadAction<string[]>) => {
      state.availableCptyIds = action.payload
      state.isLoading = false
    },
    fetchDefaultsSuccess: (state, action: PayloadAction<ApiAssetDefaultConfiguration>) => {
      state.availableAssetClasses = action.payload.assetClasses
      state.availableCurrencies = action.payload.currencies
      state.isLoading = false
    },
    search: (state, action: PayloadAction<string>) => {
      state.query.WildcardString = action.payload
    },
    filter: (state, action: PayloadAction<string[]>) => {
      state.query.Status = action.payload.join(',')
    },
    setPage: (state, action: PayloadAction<TablePaginationParams>) => {
      state.query.PageNumber = action.payload.pageNumber
      state.query.PageSize = action.payload.pageSize
      state.isLoading = true
    },
    clearFilters: state => {
      state.query.Status = ''
      state.query.WildcardString = ''
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    saveAsset: state => {
      state.serverErrors = null
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    saveExDates: (state, action: PayloadAction<string[]>) => {},
    saveExDatesSuccess: state => {
      state.exDateNotification = `Ex-dates successfully updated for ${state.selectedAsset?.asset?.name}`
    },
    saveExDatesFailure: (state, action: PayloadAction<Errors>) => {
      state.exDateNotification = undefined
      if (state.errors) {
        state.errors = { ...state.errors, ...action.payload }
      } else {
        state.errors = action.payload
      }
    },
    resetExDateNotification: state => {
      state.exDateNotification = undefined
    },
    saveAssetSuccess: state => {
      state.canProceed = false
      state.isNew = false
      state.saveSuccess = true
    },
    softDeleteAsset: state => {
      state.serverErrors = null
    },
    restoreAsset: state => {
      state.serverErrors = null
    },
    softDeleteAssetSuccess: state => {
      state.selectedAsset.asset.isDeleted = true
    },
    restoreAssetSuccess: state => {
      state.selectedAsset.asset.isDeleted = false
    },
    setSelectedAsset: (state, action: PayloadAction<string>) => {
      state.selectedAsset = state.assets.find(x => x.asset.assetId === action.payload)
      state.isNew = false
      state.saveSuccess = false
      state.errors = {}
      state.serverErrors = null
    },
    setIsNew: state => {
      state.isNew = true
      state.saveSuccess = false
      state.errors = {}
      state.serverErrors = null
      state.selectedAsset = {
        asset: {
          type: 'MutualFund',
          fundManager: '',
          isMarketDataReady: false,
          externalIdentifiers: {
            managementCompanyCode: '',
            fundCode: '',
            productCode: '',
            accountNumber: '',
            fundManagerCounterpartyId: ''
          },
          assetId: '',
          tradingProvider: '',
          marketDataProvider: '',
          isinCode: '',
          clientAssetId: '',
          name: '',
          assetClass: '',
          currencyIso: 'ZAR',
          sharePrecisionScale: 5,
          priceScalingFactor: 1,
          protectionClassification: 'Performance',
          dealingCalendar: {
            dealingPeriodExpectation: {
              executionRedemption: '2.00:00:00',
              executionSubscription: '2.00:00:00',
              confirmationRedemption: '00:00:00',
              confirmationSubscription: '00:00:00',
              settlementRedemption: 0,
              settlementSubscription: 0,
              navPublication: '1.00:00:00'
            },
            startDate: '2000-01-01',
            endDate: '2100-01-01',
            assumptions: 'Weekends',
            holidays: []
          },
          noticePeriodDays: 0,
          dealingFrequency: 'Daily',
          dealingState: 'Active',
          cutoffTimeHours: 14,
          cutoffTimeMinutes: 0,
          taxClassification: 'Yield',
          disallowedStrategies: [],
          multiplier: {
            shortLambda: null,
            longLambda: null,
            shortTermVolatilityWindow: null,
            longTermVolatilityWindow: null,
            multiplier: null,
            multiplierType: 'Fixed',
            standardMultiplier: null,
            reducedMultiplier: null,
            volatilityTrigger: null
          },
          maxLeverageRatio: null,
          startDate: getTodayAsString()
        },
        assetUsage: {},
        assetIsInUse: false
      } as any
    },
    setSaveSuccessFalse: state => {
      state.saveSuccess = false
    },
    setAssetType: (state, action: PayloadAction<ApiAssetType>) => {
      const { asset } = state.selectedAsset
      if (
        action.payload === 'MutualFund' &&
        (asset as ApiMutualFundAsset).externalIdentifiers === undefined
      ) {
        ;(asset as ApiMutualFundAsset).externalIdentifiers = {
          managementCompanyCode: '',
          fundCode: '',
          productCode: '',
          accountNumber: '',
          fundManagerCounterpartyId: ''
        }
      }
      if (
        action.payload === 'SegregatedPortfolio' &&
        (asset as ApiSegregatedPortfolioAsset).startDate === undefined
      ) {
        ;(asset as ApiSegregatedPortfolioAsset).startDate = getTodayAsString()
      }
      state.selectedAsset.asset = asset
      state.selectedAsset.asset.type = action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setAssetName: (state, action: PayloadAction<string>) => {
      state.selectedAsset.asset.name = action.payload
      delete state.errors.asset_name
      state.canProceed = true
      state.saveSuccess = false
    },
    setIsin: (state, action: PayloadAction<string>) => {
      if (state.isNew) {
        state.selectedAsset.asset.assetId = action.payload
      }
      ;(state.selectedAsset.asset as any).isinCode = action.payload
      delete state.errors.isin
      state.canProceed = true
      state.saveSuccess = false
    },

    setAssetClass: (state, action: PayloadAction<string>) => {
      state.selectedAsset.asset.assetClass = action.payload
      delete state.errors.asset_class
      state.canProceed = true
    },
    setFundManager: (state, action: PayloadAction<string>) => {
      const asset = state.selectedAsset.asset as ApiMutualFundAsset
      asset.fundManager = action.payload
      state.selectedAsset.asset = asset
      delete state.errors.fund_manager
      state.canProceed = true
      state.saveSuccess = false
    },
    setTaxClassification: (state, action: PayloadAction<ApiTaxClassification>) => {
      state.selectedAsset.asset.taxClassification = action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setDealingFrequency: (state, action: PayloadAction<ApiAssetDealingFrequency>) => {
      state.selectedAsset.asset.dealingFrequency = action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setNoticePeriodDays: (state, action: PayloadAction<number>) => {
      state.selectedAsset.asset.noticePeriodDays = action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setCutOffTime: (state, action: PayloadAction<{ hours: number; minutes: number }>) => {
      state.selectedAsset.asset.cutoffTimeHours = action.payload.hours
      state.selectedAsset.asset.cutoffTimeMinutes = action.payload.minutes
      state.canProceed = true
      state.saveSuccess = false
    },
    setSubSettlementLag: (state, action: PayloadAction<number>) => {
      state.selectedAsset.asset.dealingCalendar.dealingPeriodExpectation.settlementSubscription =
        action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setRedSettlementLag: (state, action: PayloadAction<number>) => {
      state.selectedAsset.asset.dealingCalendar.dealingPeriodExpectation.settlementRedemption =
        action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setStrategyPermission: (state, action: PayloadAction<{ id: string; val: boolean }>) => {
      const { id } = action.payload
      const { val } = action.payload
      const isAllowed = !state.selectedAsset.asset.disallowedStrategies.includes(id)
      if (isAllowed && !val) {
        state.selectedAsset.asset.disallowedStrategies.push(id)
      }
      if (!isAllowed && val) {
        const index = state.selectedAsset.asset.disallowedStrategies.indexOf(id)
        delete state.selectedAsset.asset.disallowedStrategies[index]
      }
      state.canProceed = true
      state.saveSuccess = false
    },
    setMaxLeverageRatio: (state, action: PayloadAction<number>) => {
      state.selectedAsset.asset.maxLeverageRatio = action.payload
      delete state.errors.max_lev_ratio
      state.canProceed = true
      state.saveSuccess = false
    },
    setShortLambda: (state, action: PayloadAction<number>) => {
      state.selectedAsset.asset.multiplier.shortLambda = action.payload
      delete state.errors.short_lambda
      state.canProceed = true
      state.saveSuccess = false
    },
    setLongLambda: (state, action: PayloadAction<number>) => {
      state.selectedAsset.asset.multiplier.longLambda = action.payload
      delete state.errors.long_lambda
      state.canProceed = true
      state.saveSuccess = false
    },
    setMultiplierType: (state, action: PayloadAction<ApiMultiplierType>) => {
      state.selectedAsset.asset.multiplier.multiplierType = action.payload
      const variableMultiplier = state.selectedAsset.asset.multiplier as ApiVariableMultiplier
      if (action.payload === 'Variable') {
        if (
          variableMultiplier.volatilityTrigger === null ||
          variableMultiplier.volatilityTrigger === undefined
        ) {
          variableMultiplier.volatilityTrigger = 0.05
        }
        if (
          variableMultiplier.shortTermVolatilityWindow === null ||
          variableMultiplier.shortTermVolatilityWindow === undefined
        ) {
          variableMultiplier.shortTermVolatilityWindow = 10
        }
        if (
          variableMultiplier.longTermVolatilityWindow === null ||
          variableMultiplier.longTermVolatilityWindow === undefined
        ) {
          variableMultiplier.longTermVolatilityWindow = 10
        }
      }
      state.selectedAsset.asset.multiplier = variableMultiplier
      state.canProceed = true
      state.saveSuccess = false
    },
    setFixedMultiplier: (state, action: PayloadAction<number>) => {
      if (state.selectedAsset.asset.multiplier.multiplierType === 'Fixed') {
        ;(state.selectedAsset.asset as any).multiplier = {
          multiplier: action.payload,
          shortLambda: state.selectedAsset.asset.multiplier.shortLambda,
          longLambda: state.selectedAsset.asset.multiplier.longLambda,
          multiplierType: 'Fixed'
        }
        delete state.errors.fixed_multiplier
        state.canProceed = true
        state.saveSuccess = false
      } else {
        // What to do if the multiplier type is not fixed?
      }
    },
    setShortTermVolWindow: (state, action: PayloadAction<number>) => {
      if (state.selectedAsset.asset.multiplier.multiplierType === 'Variable') {
        const variableMultiplier = state.selectedAsset.asset.multiplier as ApiVariableMultiplier
        variableMultiplier.shortTermVolatilityWindow = action.payload
        state.selectedAsset.asset.multiplier = variableMultiplier
        state.canProceed = true
        state.saveSuccess = false
      } else {
        // What to do if the multiplier type is not variable?
      }
    },
    setLongTermVolWindow: (state, action: PayloadAction<number>) => {
      if (state.selectedAsset.asset.multiplier.multiplierType === 'Variable') {
        const variableMultiplier = state.selectedAsset.asset.multiplier as ApiVariableMultiplier
        variableMultiplier.longTermVolatilityWindow = action.payload
        state.selectedAsset.asset.multiplier = variableMultiplier
        state.canProceed = true
        state.saveSuccess = false
      } else {
        // What to do if the multiplier type is not variable?
      }
    },
    setStandardMultiplier: (state, action: PayloadAction<number>) => {
      if (state.selectedAsset.asset.multiplier.multiplierType === 'Variable') {
        const variableMultiplier = state.selectedAsset.asset.multiplier as ApiVariableMultiplier
        variableMultiplier.standardMultiplier = action.payload
        state.selectedAsset.asset.multiplier = variableMultiplier
        delete state.errors.standard_multiplier
        delete state.errors.reduced_vs_std_multiplier
        state.canProceed = true
        state.saveSuccess = false
      } else {
        // What to do if the multiplier type is not variable?
      }
    },
    setReducedMultiplier: (state, action: PayloadAction<number>) => {
      if (state.selectedAsset.asset.multiplier.multiplierType === 'Variable') {
        const variableMultiplier = state.selectedAsset.asset.multiplier as ApiVariableMultiplier
        variableMultiplier.reducedMultiplier = action.payload
        state.selectedAsset.asset.multiplier = variableMultiplier
        delete state.errors.reduced_multiplier
        delete state.errors.reduced_vs_std_multiplier
        state.canProceed = true
        state.saveSuccess = false
      } else {
        // What to do if the multiplier type is not variable?
      }
    },
    setVolTriggerLevel: (state, action: PayloadAction<number>) => {
      if (state.selectedAsset.asset.multiplier.multiplierType === 'Variable') {
        const variableMultiplier = state.selectedAsset.asset.multiplier as ApiVariableMultiplier
        variableMultiplier.volatilityTrigger = action.payload
        state.selectedAsset.asset.multiplier = variableMultiplier
        state.canProceed = true
        state.saveSuccess = false
      } else {
        // What to do if the multiplier type is not variable?
      }
    },
    setProtectionClassification: (
      state,
      action: PayloadAction<ApiAssetProtectionClassification>
    ) => {
      state.selectedAsset.asset.protectionClassification = action.payload
      state.canProceed = true
      state.saveSuccess = false
      if (action.payload === 'Performance') {
        ;(state.selectedAsset.asset as any).multiplier = {
          multiplierType: 'Fixed',
          multiplier: null,
          shortLambda: null,
          longLambda: null
        }
      }
    },
    setCurrency: (state, action: PayloadAction<string>) => {
      state.selectedAsset.asset.currencyIso = action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setPriceScalingFactor: (state, action: PayloadAction<number>) => {
      state.selectedAsset.asset.priceScalingFactor = action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setManagementCoCode: (state, action: PayloadAction<string>) => {
      const asset = state.selectedAsset.asset as ApiMutualFundAsset
      asset.externalIdentifiers.managementCompanyCode = action.payload
      state.selectedAsset.asset = asset
      delete state.errors.manco_code
      state.canProceed = true
      state.saveSuccess = false
    },
    setFundCode: (state, action: PayloadAction<string>) => {
      const asset = state.selectedAsset.asset as ApiMutualFundAsset
      asset.externalIdentifiers.fundCode = action.payload
      state.selectedAsset.asset = asset
      delete state.errors.fund_code
      state.canProceed = true
      state.saveSuccess = false
    },
    setProductCode: (state, action: PayloadAction<string>) => {
      const asset = state.selectedAsset.asset as ApiMutualFundAsset
      asset.externalIdentifiers.productCode = action.payload
      state.selectedAsset.asset = asset
      delete state.errors.product_code
      state.canProceed = true
      state.saveSuccess = false
    },
    setAccountNumber: (state, action: PayloadAction<string>) => {
      const asset = state.selectedAsset.asset as ApiMutualFundAsset
      asset.externalIdentifiers.accountNumber = action.payload
      state.selectedAsset.asset = asset
      delete state.errors.account_number
      state.canProceed = true
      state.saveSuccess = false
    },
    setFundMgrCptyId: (state, action: PayloadAction<string>) => {
      const asset = state.selectedAsset.asset as ApiMutualFundAsset
      asset.externalIdentifiers.fundManagerCounterpartyId = action.payload
      state.selectedAsset.asset = asset
      delete state.errors.fund_mgr_cpty
      state.canProceed = true
      state.saveSuccess = false
    },
    addNewFee: (state, action: PayloadAction<ApiFee>) => {
      state.selectedAsset.asset.fees.push(action.payload)
      state.canProceed = true
    },
    removeFee: (state, action: PayloadAction<number>) => {
      state.selectedAsset.asset.fees.splice(action.payload, 1)
      state.canProceed = true
    },
    changeFeeProperty: (
      state,
      action: PayloadAction<{ index: number; property: string; updatedValue: any }>
    ) => {
      set(
        state.selectedAsset.asset.fees[action.payload.index],
        action.payload.property,
        action.payload.updatedValue
      )
      state.canProceed = true
      delete state.errors.feeRates
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType as any, reducer)
    })
  }
})

export const { actions } = editAssetsSlice
export type EditAssetsActions = typeof actions

function saveAssetSaga(onSuccess: PutEffect, onFail: (e: any) => PutEffect) {
  return function* saveAsset2Saga() {
    const state = yield select()
    const { editAssets } = state
    const { selectedAsset } = editAssets

    const result = validate(editAssets)
    if (!result.isValid) {
      yield put(actions.setInvalid(result.errors))
    } else {
      if (!selectedAsset.asset.assetId) {
        throw new Error('FATAL ERROR: Asset Id does not exist')
      }
      try {
        if (selectedAsset.asset.type === 'SegregatedPortfolio') {
          const segPortfolio = mapAssetToSegPortfolio(
            selectedAsset.asset as ApiSegregatedPortfolioAsset
          )
          if (editAssets.isNew) {
            yield call(api.tenant.segregatedportfolioCreate, TENANT_ID, segPortfolio)
            yield onSuccess
          } else {
            yield call(api.tenant.segregatedportfolioUpdate, TENANT_ID, segPortfolio)
            yield onSuccess
          }
        } else if (selectedAsset.asset.type === 'MutualFund') {
          const mutualFund = mapAssetToMutualFund(selectedAsset.asset)
          if (editAssets.isNew) {
            yield call(api.tenant.mutualfundCreate, TENANT_ID, mutualFund)
            yield onSuccess
          } else {
            yield call(api.tenant.mutualfundUpdate, TENANT_ID, mutualFund)
            yield onSuccess
          }
        } else {
          throw new Error('FATAL ERROR: Invalid asset type')
        }
      } catch (error) {
        yield onFail(error)
      }
    }
  }
}

function* saveExDatesSaga(action: PayloadAction<string[]>) {
  const state = yield select()
  const { editAssets } = state
  const { selectedAsset } = editAssets
  const data: ApiAssetExDates = { exDates: [] }
  if (action.payload) {
    action.payload.forEach(selectedDate => {
      data.exDates.push(selectedDate)
    })
  }
  const result = validate(editAssets)
  if (!result.isValid) {
    yield put(actions.setInvalid(result.errors))
  } else {
    if (!selectedAsset.asset.assetId) {
      throw new Error('FATAL ERROR: Asset Id does not exist')
    }
    try {
      yield call(api.tenant.exdatesAddCreate, selectedAsset.asset.assetId, TENANT_ID, data)
      yield put({ type: 'editAssets/saveExDatesSuccess' })
    } catch (error) {
      yield put({
        type: 'editAssets/saveExDatesFailure',
        payload: extractErrorMessagesFromServerResponse(error)
      })
    }
  }
}

function* fetchAssetDetailSaga() {
  const rootState = yield select()
  const state = rootState.editAssets

  const { query } = state

  try {
    const response = yield call(api.tenant.listDetail, TENANT_ID, query)
    yield put({
      type: 'editAssets/fetchAssetDetailSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editAssets/fetchAssetDetailFailure', payload: error })
  }
}

function* fetchStrategyTemplatesSaga() {
  try {
    const response = yield call(api.tenant.strategyDetail, TENANT_ID)
    yield put({
      type: 'editAssets/fetchStrategiesSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editAssets/fetchStrategiesFailure', payload: error })
  }
}

function* fetchCptyIdsSaga() {
  try {
    const response = yield call(api.tenant.counterpartyidsDetail, TENANT_ID)
    yield put({
      type: 'editAssets/fetchCptyIdsSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editAssets/fetchCptyIdsFailure', payload: error })
  }
}

function* fetchAssetPriceHistorySaga() {
  const rootState = yield select()
  const state = rootState.editAssets
  const query = state.selectedAsset.asset.assetId
  try {
    const response = yield call(api.tenant.historyDetail2, query, TENANT_ID)
    yield put({
      type: 'editAssets/fetchAssetPriceHistorySuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editAssets/fetchAssetPriceHistoryFailure', payload: error })
  }
}

function* fetchAssetDefaultsSaga() {
  try {
    const response = yield call(api.tenant.defaultsDetail, TENANT_ID)
    yield put({
      type: 'editAssets/fetchDefaultsSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editAssets/fetchDefaultsFailure', payload: error })
  }
}

function* fetchInitialDataSaga() {
  yield all([
    fetchAssetDetailSaga(),
    fetchStrategyTemplatesSaga(),
    fetchAssetDefaultsSaga(),
    fetchCptyIdsSaga()
  ])
}

function* softDeleteAssetSaga() {
  const state = yield select()
  const { editAssets } = state
  const { selectedAsset } = editAssets
  if (!selectedAsset.asset.assetId) {
    throw new Error('FATAL ERROR: Asset Id does not exist')
  }
  try {
    yield call(api.tenant.softdeleteCreate, selectedAsset.asset.assetId, TENANT_ID)
    yield put(actions.softDeleteAssetSuccess())
  } catch (error) {
    yield put(actions.saveError(error))
  }
}

function* restoreAssetSaga() {
  const state = yield select()
  const { editAssets } = state
  const { selectedAsset } = editAssets
  if (!selectedAsset.asset.assetId) {
    throw new Error('FATAL ERROR: Asset Id does not exist')
  }
  try {
    yield call(api.tenant.restoreCreate, selectedAsset.asset.assetId, TENANT_ID)
    yield put(actions.restoreAssetSuccess())
  } catch (error) {
    yield put(actions.saveError(error))
  }
}

export function* editAssetRootSaga() {
  yield all([
    takeLatest('editAssets/init', fetchInitialDataSaga),
    takeLatest('editAssets/search', fetchAssetDetailSaga),
    takeLatest('editAssets/setPage', fetchAssetDetailSaga),
    takeLatest(
      'editAssets/saveAsset',
      saveAssetSaga(put(actions.saveAssetSuccess()), e => put(actions.saveError(e)))
    ),
    takeLatest('editAssets/saveAssetSuccess', fetchAssetDetailSaga),
    takeLatest('editAssets/setSelectedAsset', fetchAssetPriceHistorySaga),
    takeLatest('editAssets/saveExDates', saveExDatesSaga),
    takeLatest('editAssets/saveExDatesSuccess', fetchAssetDetailSaga),
    takeLatest('editAssets/softDeleteAsset', softDeleteAssetSaga),
    takeLatest('editAssets/restoreAsset', restoreAssetSaga),
    takeLatest('editAssets/softDeleteAssetSuccess', fetchAssetDetailSaga),
    takeLatest('editAssets/restoreAssetSuccess', fetchAssetDetailSaga)
  ])
}

export default editAssetsSlice.reducer
